import { commonAPI } from '@/services-ts/axios'
import type { ISurveyQuestion } from '@/types/Data'
import type {
  ICreateSurveyQuestionGroup,
  ICreateSurveyQuestionImage,
  IGetSurveyList,
  IPayloadSurvey,
  IUpdateSurveyQuestion,
  IUpdateSurveyQuestionGroup,
} from '@/types/Request'
import type { ISurveyStore } from '@/types/Store/State'
import { Alert, Format } from '@/utils-ts'
import { defineStore } from 'pinia'

export const useSurveyStore = defineStore('survey', {
  state: (): ISurveyStore => ({
    surveyList: {
      data: [],
      total: 0,
    },
    surveyQuestionGroupList: [],
  }),

  actions: {
    async GetSurveyList(payload: IGetSurveyList) {
      const { status, data, total } = await commonAPI.SurveyServices.GetSurveyList(payload)
      this.surveyList = status ? { data: data || [], total: total || 0 } : { data: [], total: 0 }
    },

    async CreateSurvey(payload: IPayloadSurvey) {
      const { status } = await commonAPI.SurveyServices.CreateSurvey(payload)

      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async UpdateSurvey(payload: IPayloadSurvey, isStatus: boolean = false) {
      const { status } = await commonAPI.SurveyServices.UpdateSurvey(payload)

      if (status) {
        if (isStatus) {
          const item = this.surveyList.data.find(x => x.id === payload.id)
          if (item) item.isActive = !item.isActive
        }

        void Alert({ message: Format('admin_global_updated'), type: 'success' })
      }
      return status
    },

    async DeleteSurvey(Id: string) {
      const { status } = await commonAPI.SurveyServices.DeleteSurvey(Id)

      if (status) {
        this.surveyList.data = this.surveyList.data.filter(item => item.id !== Id)

        void Alert({ message: Format('admin_global_deleted'), type: 'success' })
      }
    },

    async GetSurveyQuestionGroupList(SurveyId: string) {
      const { status, data } = await commonAPI.SurveyServices.GetSurveyQuestionGroupList(SurveyId)

      this.surveyQuestionGroupList = status ? data || [] : []
    },

    async CreateSurveyQuestionGroup(payload: ICreateSurveyQuestionGroup) {
      const { status, data } = await commonAPI.SurveyServices.CreateSurveyQuestionGroup(payload)
      if (status) {
        this.surveyQuestionGroupList.push({
          id: data,
          ...payload,
          parentId: null,
          questions: [],
        })

        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async UpdateSurveyQuestionGroup(payload: IUpdateSurveyQuestionGroup) {
      const { status } = await commonAPI.SurveyServices.UpdateSurveyQuestionGroup(payload)

      if (status) {
        this.surveyQuestionGroupList = payload.surveyQuestionGroups

        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async DeleteSurveyQuestionGroup(Id: string) {
      const { status } = await commonAPI.SurveyServices.DeleteSurveyQuestionGroup(Id)

      if (status) {
        this.surveyQuestionGroupList = this.surveyQuestionGroupList.filter(x => x.id !== Id)

        void Alert({ message: Format('admin_global_deleted'), type: 'success' })
      }
    },

    async UpdateSurveyQuestion(payload: IUpdateSurveyQuestion) {
      const surveyImage = payload.image
      delete payload.image

      const { status, data } = await commonAPI.SurveyServices.UpdateSurveyQuestion(payload)

      if (status) {
        const group = this.surveyQuestionGroupList.find(g => g.id === payload.surveyQuestionGroupId)
        if (!group) return
        const index = group.questions.findIndex(q => q.id === data.id)

        if (index === -1) {
          group.questions.push(data)
        } else {
          group.questions[index] = data
        }

        if (surveyImage && surveyImage instanceof Blob && typeof data.id === 'string') {
          const image = {
            surveyQuestionId: data.id,
            image: surveyImage,
          }
          await this.CreateSurveyQuestionImage(image, data.surveyQuestionGroupId)
        }

        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async DeleteSurveyQuestion(payload: ISurveyQuestion) {
      const { status } = await commonAPI.SurveyServices.DeleteSurveyQuestion(payload.id!)

      if (status) {
        const group = this.surveyQuestionGroupList.find(g => g.id === payload.surveyQuestionGroupId)

        if (!group) return

        group.questions = group.questions.filter(q => q.id !== payload.id)

        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
    },

    async CreateSurveyQuestionImage(payload: ICreateSurveyQuestionImage, groupId: string) {
      const { status } = await commonAPI.SurveyServices.CreateSurveyQuestionImage(payload)

      if (status) {
        const group = this.surveyQuestionGroupList.find(g => g.id === groupId)
        if (!group) return
        const question = group.questions.find(q => q.id === payload.surveyQuestionId)
        if (!question) return
        question.imageUrl = URL.createObjectURL(payload.image)
      }
    },
    async CloneSurvey(SurveyId: string) {
      const { status } = await commonAPI.SurveyServices.CloneSurvey(SurveyId)
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async ResetSurvey(SurveyId: string) {
      const { status } = await commonAPI.SurveyServices.ResetSurvey(SurveyId)
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
    },
  },
})
